import { useEditor, EditorContent, mergeAttributes } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import suggestion from "./suggestion";
import { Check, Undo2, Brain } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { RichTextValue } from "../RichTextField";
import CustomMention from "./CustomMention";
import useEditorEventListeners from "./useEditorEventListeners";
import { NodeSelection } from "@tiptap/pm/state";

import "./styles.css";
import { Markdown } from "tiptap-markdown";
import { debounce } from "lodash";
import AIDialogEditor from "./AIDialogTipTapEditor";
import { Toolbar } from "./TiptapEditor";
import { handleChipClickRaw, injectMentionsIntoDoc } from "./chipUtils";
import {
  parseMentionToken,
  getEditorMentionIds,
  getEditorMentionClasses,
  chipOptionsToSuggestionItems,
  allChipValues,
  hasChipValues,
  type AdvancedChipOption,
  type MentionSuggestionItem,
} from "./chipOptionsConfig";
import { AnimatePresence } from "framer-motion";
import { Banner } from "@/components/Banner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/shadcn/button";
import ReasoningDialog from "./ReasoningDialog";
import { Textarea } from "@/components/shadcn/textarea";
import { useHeuristicEvaluation } from "@/hooks/useHeuristicEvaluation";
import { HeuristicScoreBadge } from "./HeuristicScoreBadge";
import { Separator } from "@/components/shadcn/separator";
import { HeuristicEvaluationResponse } from "@/generated";
import { ChipInsertDropdown, ChipBadgeList } from "./MentionList";
import { sanitizeTiptapContent } from "./sanitize";

function transformMentions(text: string): string {
  const mentionPattern =
    /<span data-type="mention"[^>]*?data-id="([^"]+)"[^>]*?data-prefix="([^"]*)"[^>]*>[^<]+<\/span>/g;
  return text.replace(mentionPattern, (_, dataId, prefix) => {
    return prefix ? `((${prefix}${dataId}))` : `((${dataId}))`;
  });
}

// The document a value opens as: its definition when it has content, else
// its text (the backend strips definitions when it persists, or stores an
// empty document next to the text).
function editorContent(value: RichTextValue | undefined) {
  const def = value?.definition as Record<string, unknown> | undefined;
  const empty =
    !def ||
    Object.keys(def).length === 0 ||
    (Array.isArray(def.content) && def.content.length === 0);
  if (!empty) {
    return (
      sanitizeTiptapContent(def as Record<string, unknown>) || value?.text || ""
    );
  }
  return value?.text || "";
}

function buildExtensions(placeholder?: string) {
  return [
    StarterKit.configure({ gapcursor: false }),
    CustomMention.configure({
      HTMLAttributes: {
        class: "mention",
        draggable: "true",
      },
      suggestion: {
        ...suggestion,
        items: ({ query, editor }): MentionSuggestionItem[] => {
          const suggestionItems: MentionSuggestionItem[] =
            // @ts-expect-error suggestionItems is a custom prop used by the mention extension
            editor.options.editorProps.suggestionItems || [];
          return suggestionItems.filter((item) =>
            item.id.toLowerCase().startsWith(query.toLowerCase())
          );
        },
      },
      renderText({ node }) {
        const prefix = node.attrs.prefix || "";
        return `((${prefix}${node.attrs.id}))`;
      },
      renderHTML({ options, node }) {
        const color = node.attrs.color;
        const hasColor = color && color !== "default";
        const nodeImage = node.attrs.nodeImage as string | null;
        const nodeColor = node.attrs.nodeColor as string | null;
        const nodeName = node.attrs.nodeName as string | null;
        const nodeKind = node.attrs.nodeKind as "node" | "secret" | null;
        const hasSourceMeta = !!(
          nodeImage ||
          nodeColor ||
          nodeName ||
          nodeKind === "secret"
        );

        const buildChildren = (): any[] => {
          if (!hasSourceMeta) return [node.attrs.id];
          const children: any[] = [];
          children.push([
            "span",
            {
              class:
                "chip-source-avatar inline-flex items-center justify-center rounded-[3px] shrink-0 overflow-hidden border border-black/10 mr-1 align-middle",
              style: `width:0.75rem;height:0.75rem;${
                nodeColor ? `background-color:${nodeColor};` : ""
              }`,
              contenteditable: "false",
            },
            ...(nodeImage
              ? [
                  [
                    "img",
                    {
                      src: nodeImage,
                      alt: "",
                      class: "object-contain",
                      style: "width:0.625rem;height:0.625rem;",
                      draggable: "false",
                    },
                  ],
                ]
              : nodeKind === "secret"
                ? [
                    [
                      "span",
                      {
                        class: "mention-source-avatar-key",
                        style: "font-size:0.625rem;line-height:1;",
                        "aria-hidden": "true",
                      },
                      "🔑",
                    ],
                  ]
                : []),
          ]);
          if (nodeName) {
            children.push([
              "span",
              {
                class: "chip-source-name opacity-60 mr-1",
                contenteditable: "false",
              },
              nodeName,
            ]);
          }
          children.push(node.attrs.id);
          return children;
        };

        if (hasColor) {
          return [
            "span",
            {
              ...mergeAttributes(options.HTMLAttributes),
              class: getEditorMentionClasses(color, node.attrs.class),
              "data-icon": node.attrs.icon || "",
            },
            ...buildChildren(),
          ];
        }

        return [
          "span",
          {
            ...mergeAttributes(options.HTMLAttributes),
            class: `mention ${node.attrs.class || ""}`,
          },
          ...buildChildren(),
        ];
      },
    }),
    Placeholder.configure({ placeholder }),
    Markdown,
  ];
}

interface AITiptapEditorProps {
  onChange: (value: RichTextValue[]) => any;
  chipOptions?: AdvancedChipOption[];
  value?: RichTextValue[];
  fullSize?: boolean;
  /** Focus the editor on mount. Off by default so an editor that fills its
   *  pane does not steal focus from fields beside it. */
  autoFocus?: boolean;
  unselectAllNodes?: () => any;
  placeholder?: string;
  isControlled?: boolean;
  label?: string;
  isDisabled?: boolean;
  improverPrompt?: string;
  initialHeuristicResults?: HeuristicEvaluationResponse | null;
  enableHeuristics?: boolean;
  heuristicContext?: string;
  heuristicTypes?: string[];
  improverContext?: string;
  isFlush?: boolean;
}

const AITiptapEditor = ({
  onChange,
  chipOptions,
  value,
  fullSize = false,
  autoFocus = false,
  unselectAllNodes,
  placeholder,
  isControlled = false,
  label,
  isDisabled = false,
  improverPrompt,
  initialHeuristicResults,
  enableHeuristics = false,
  heuristicContext,
  heuristicTypes,
  improverContext,
  isFlush = false,
}: AITiptapEditorProps) => {
  const [reasoningContent, setReasoningContent] = useState<string>("");
  const usedValue =
    value && value?.length > 0 ? value[value.length - 1] : undefined;
  const [isRaw, setIsRaw] = useState<boolean>(!!usedValue?.isRaw);
  const rawTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isProgrammaticChangeRef = useRef(false);
  // The text the editor last showed or emitted. A value that differs from it
  // was set from outside (Genie changed the agent, the form was reset).
  const shownTextRef = useRef(usedValue?.text ?? "");
  const isRawRef = useRef<boolean>(!!usedValue?.isRaw);

  const normalizedGroups = useMemo(() => chipOptions ?? [], [chipOptions]);

  const suggestionItems = useMemo(
    () => chipOptionsToSuggestionItems(normalizedGroups),
    [normalizedGroups]
  );

  const allValues = useMemo(
    () => allChipValues(normalizedGroups),
    [normalizedGroups]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce(
      (editor) => {
        if (isProgrammaticChangeRef.current) {
          isProgrammaticChangeRef.current = false;
          return;
        }
        if (isRawRef.current) {
          return;
        }

        const newContent =
          !value || value?.length === 0
            ? [
                {
                  text: transformMentions(
                    editor.storage.markdown.getMarkdown()
                  ),
                  definition: editor.getJSON(),
                },
              ]
            : value?.map((v, index) => {
                if (index !== value.length - 1) {
                  return v;
                }
                return {
                  text: transformMentions(
                    editor.storage.markdown.getMarkdown()
                  ),
                  definition: editor.getJSON(),
                };
              }) || [];
        shownTextRef.current = newContent[newContent.length - 1]?.text ?? "";
        onChange(newContent);
      },
      isControlled ? 0 : 500
    ),
    [onChange]
  );

  const extensions = useMemo(() => buildExtensions(placeholder), [placeholder]);

  const editor = useEditor({
    extensions,
    editorProps: {
      attributes: {
        class: `tiptap-editor-custom ${isDisabled ? "disabled" : ""}`,
      },
      // @ts-expect-error suggestionItems is a custom prop used by the mention extension
      suggestionItems: suggestionItems,
    },
    content: editorContent(usedValue),
    onUpdate({ editor }) {
      if (isRawRef.current) return;
      debouncedOnChange(editor);
    },
    onSelectionUpdate({ editor }) {
      const { from, to } = editor.state.selection;

      if (editor.state.selection instanceof NodeSelection) {
        return;
      }

      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "mention") {
          const mentionFrom = pos;
          const mentionTo = pos + node.nodeSize;
          const isFullySelected = from <= mentionFrom && to >= mentionTo;

          editor.commands.command(({ tr }) => {
            tr.setMeta("addToHistory", false);
            if (isFullySelected) {
              tr.setNodeMarkup(pos, null, { ...node.attrs, class: "selected" });
            } else {
              tr.setNodeMarkup(pos, null, { ...node.attrs, class: null });
            }
            return true;
          });
        }
      });
    },
    immediatelyRender: false,
    autofocus: autoFocus,
  });

  useEffect(() => {
    isRawRef.current = !!usedValue?.isRaw;
    setIsRaw(!!usedValue?.isRaw);
  }, [usedValue?.isRaw]);

  useEffect(() => {
    const text = usedValue?.text ?? "";
    if (!editor || isRaw || text === shownTextRef.current) return;
    shownTextRef.current = text;
    if (text === transformMentions(editor.storage.markdown.getMarkdown()))
      return;
    editor.commands.setContent(editorContent(usedValue), false);
    if (editorContent(usedValue) === text && text.includes("(("))
      editor.commands.setContent(
        injectMentionsIntoDoc(editor.getJSON(), normalizedGroups),
        false
      );
  }, [editor, isRaw, usedValue, normalizedGroups]);

  useEffect(() => {
    if (editor && editor.isEditable !== !isDisabled) {
      isProgrammaticChangeRef.current = true;
      editor.setEditable(!isDisabled);
    }
  }, [editor, isDisabled]);

  const {
    addCodeBlock,
    addListBlock,
    addMention,
    addReferenceMention,
    toggleBold,
    toggleItalic,
  } = useEditorEventListeners({
    editor,
    suggestionItems: chipOptionsToSuggestionItems(chipOptions ?? []),
    unselectAllNodes,
    fullSize,
  });

  const [isOpen, setIsOpen] = useState(false);
  const hasAutoRetriedOnModalOpenRef = useRef(false);
  const [dialogInitialHeuristicsOpen, setDialogInitialHeuristicsOpen] =
    useState(false);
  const [isReasoningOpen, setIsReasoningOpen] = useState(false);
  const [isOptimizerOpen, setIsOptimizerOpen] = useState(false);

  const text = isRaw ? usedValue?.text : editor?.getText();

  const activeTags = useMemo(() => {
    if (!isRaw && editor) {
      return getEditorMentionIds(editor.getJSON());
    }
    return Array.from(
      new Set(
        text
          ?.match(/\(\(@?[^()]*?\)\)/g)
          ?.map((tag) => parseMentionToken(tag).id) || []
      )
    );
  }, [text, isRaw, editor]);

  const [heuristicTextOverride, setHeuristicTextOverride] = useState<
    string | null
  >(null);
  const heuristicText = heuristicTextOverride ?? usedValue?.text ?? "";

  const handleHeuristicTextChange = useCallback(
    (text: string | null) => setHeuristicTextOverride(text),
    []
  );

  const {
    results: heuristicResults,
    isLoading: isLoadingHeuristics,
    isError: isHeuristicsError,
    errorMessage: heuristicsErrorMessage,
    isOutdated: isHeuristicsOutdated,
    canAnalyse: canAnalyseHeuristics,
    triggerEvaluation: triggerHeuristicEvaluation,
    scheduleReanalysis: scheduleHeuristicReanalysis,
    revertAnalysis: revertHeuristicAnalysis,
  } = useHeuristicEvaluation({
    text: heuristicText,
    enabled: enableHeuristics && !isRaw,
    context: heuristicContext,
    heuristicTypes,
  });

  useEffect(() => {
    if (value && value[value.length - 1]?.text && isControlled && editor) {
      const tagsUsedOnValue = Array.from(
        new Set(
          value[0].text
            .match(/\(\(@?[^()]*?\)\)/g)
            ?.map((tag) => parseMentionToken(tag).id) || []
        )
      );
      const tagsUsedOnText = getEditorMentionIds(editor.getJSON());

      const extraTags = tagsUsedOnText.filter(
        (tag) => !tagsUsedOnValue.includes(tag)
      );

      if (extraTags.length > 0 && tagsUsedOnValue.length > 0) {
        const { from, to } = editor.state.selection;

        editor
          .chain()
          .setContent(sanitizeTiptapContent(value[0].definition) || "")
          .setTextSelection({ from, to })
          .run();
      }
    }
  }, [value, isControlled, editor, chipOptions, normalizedGroups]);

  const handleModalSave = (newContent: RichTextValue[]) => {
    onChange(newContent);

    if (editor) {
      isProgrammaticChangeRef.current = true;
      editor.commands.setContent(
        newContent[newContent.length - 1]?.definition
          ? sanitizeTiptapContent(
              newContent[newContent.length - 1].definition
            ) || ""
          : ""
      );
    }

    setHeuristicTextOverride(null);
  };

  const removeLastValue = useCallback(() => {
    if (editor) {
      isProgrammaticChangeRef.current = true;
      onChange(value?.slice(0, -1) || []);
    }
  }, [editor, onChange, value]);

  const removeFirstValue = useCallback(() => {
    if (editor) {
      isProgrammaticChangeRef.current = true;
      onChange(value?.slice(1) || []);
    }
  }, [editor, onChange, value]);

  const handleRevertChange = () => {
    if (!value || value.length <= 1) return;
    editor?.commands.setContent(
      sanitizeTiptapContent(value[0]?.definition) || ""
    );
    removeLastValue();
    revertHeuristicAnalysis?.();
  };

  const handleAcceptChange = () => {
    removeFirstValue();
  };

  const invalidTags = useMemo(() => {
    return Array.from(
      new Set(activeTags.filter((tag) => !allValues.includes(tag)) || [])
    );
  }, [activeTags, allValues]);

  const convertRawToDefinition = (plain: string) => {
    if (!plain || plain.length === 0) return {} as Record<string, any>;
    if (editor) {
      editor.commands.setContent(plain);
      const parsed = editor.getJSON();
      const withMentions = injectMentionsIntoDoc(parsed, normalizedGroups);
      editor.commands.setContent(withMentions);
      return withMentions;
    }
    return {
      type: "doc",
      content: [
        { type: "paragraph", content: [{ type: "text", text: plain }] },
      ],
    } as Record<string, any>;
  };

  // One-time: on editor mount, convert ((id)) text tokens to mention nodes when
  // the value has no definition (e.g. loaded from backend which strips definition).
  const didInitInjectRef = useRef(false);
  useEffect(() => {
    if (!editor || didInitInjectRef.current) return;
    const hasDefinition =
      usedValue?.definition &&
      Object.keys(usedValue.definition as object).length > 0;
    if (hasDefinition || !usedValue?.text?.includes("((")) return;
    didInitInjectRef.current = true;
    const withMentions = injectMentionsIntoDoc(editor.getJSON());
    editor.commands.setContent(withMentions, false);
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLastValue = (
    updater: (v: RichTextValue | undefined) => RichTextValue
  ) => {
    if (!value || value.length === 0) {
      onChange([updater(undefined)]);
    } else {
      const next = value.map((v, i) =>
        i === value.length - 1 ? updater(v) : v
      );
      onChange(next);
    }
  };

  const toggleRawMode = () => {
    if (isRaw) {
      const nextDefinition = convertRawToDefinition(usedValue?.text || "");
      setIsRaw(false);
      updateLastValue((v) => ({
        text: v?.text || "",
        definition: nextDefinition,
        isRaw: false,
      }));
    } else {
      setIsRaw(true);
      updateLastValue((v) => ({
        text: v?.text || "",
        definition: {},
        isRaw: true,
      }));
    }
  };

  const heuristicScore =
    heuristicResults?.weighted_score != null
      ? heuristicResults.weighted_score * 10
      : null;

  const hasChipOptions = hasChipValues(normalizedGroups);

  const chipDropdown = (
    <ChipInsertDropdown
      items={suggestionItems}
      isDisabled={isDisabled || !hasChipOptions}
      onSelect={(item) => {
        if (isRaw) {
          handleChipClickRaw(item.id, {
            textareaRef: rawTextareaRef,
            currentText: usedValue?.text || "",
            prefix: item.prefix,
            setText: (next) =>
              updateLastValue(() => ({
                text: next,
                definition: {},
                isRaw: true,
              })),
          });
        } else {
          addMention(item.id, item.prefix, item.color, {
            nodeImage: item.sourceImage,
            nodeColor: item.sourceColor,
            nodeName: item.sourceName,
            nodeKind: item.sourceKind,
          });
        }
      }}
    />
  );

  const extraToolbarContent = (
    <div className="flex items-center gap-1">
      {enableHeuristics && !isRaw && (
        <>
          <HeuristicScoreBadge
            score={heuristicScore}
            isLoading={isLoadingHeuristics}
            isOutdated={isHeuristicsOutdated}
            isError={isHeuristicsError}
            errorMessage={heuristicsErrorMessage}
            onClick={() => {
              setDialogInitialHeuristicsOpen(true);
              setIsOpen(true);
              if (isHeuristicsError && !hasAutoRetriedOnModalOpenRef.current) {
                hasAutoRetriedOnModalOpenRef.current = true;
                triggerHeuristicEvaluation(true); // silent retry on first open
              } else if (
                !heuristicResults &&
                canAnalyseHeuristics &&
                !isHeuristicsError
              ) {
                triggerHeuristicEvaluation();
              }
            }}
          />
          {hasChipOptions && suggestionItems && suggestionItems.length > 0 && (
            <Separator orientation="vertical" className="ml-1" />
          )}
        </>
      )}
      {chipDropdown}
      {(enableHeuristics || hasChipOptions) && (
        <Separator orientation="vertical" />
      )}
    </div>
  );

  return (
    <>
      <div
        className={cn("flex flex-col w-full flex-1 max-h-full")}
        aria-disabled={isDisabled}
      >
        {hasChipOptions && (
          <ChipBadgeList
            chipOptions={normalizedGroups}
            activeTags={activeTags}
            isDisabled={isDisabled}
            onSelect={(item) => {
              if (isRaw) {
                handleChipClickRaw(item.id, {
                  textareaRef: rawTextareaRef,
                  currentText: usedValue?.text || "",
                  prefix: item.prefix,
                  setText: (next) =>
                    updateLastValue(() => ({
                      text: next,
                      definition: {},
                      isRaw: true,
                    })),
                });
              } else {
                addMention(item.id, item.prefix, item.color, {
                  nodeImage: item.sourceImage,
                  nodeColor: item.sourceColor,
                  nodeName: item.sourceName,
                  nodeKind: item.sourceKind,
                });
              }
            }}
          />
        )}

        <AnimatePresence>
          {invalidTags.length > 0 && (
            <div className="p-2">
              <Banner
                style="warning"
                label={`The following tags are not valid: ${invalidTags.join(
                  ", "
                )}`}
              />
            </div>
          )}
        </AnimatePresence>

        <div
          className={cn(
            "flex flex-col flex-1 overflow-hidden relative max-h-full cursor-auto",
            isFlush
              ? "focus-within:outline-none"
              : "border border-input rounded-md focus-within:ring-ring focus-within:ring-[1px] focus-within:outline-none",
            isDisabled
              ? "cursor-not-allowed opacity-50"
              : "cursor-auto opacity-100"
          )}
        >
          <Toolbar
            addCodeBlock={addCodeBlock}
            addListBlock={addListBlock}
            toggleBold={toggleBold}
            toggleItalic={toggleItalic}
            editor={editor}
            onOpen={() => {
              setDialogInitialHeuristicsOpen(false);
              setIsOpen(true);
            }}
            fullSize={fullSize}
            config={{ isExpandable: true }}
            isDisabled={isDisabled}
            isRaw={isRaw}
            onToggleRaw={toggleRawMode}
            extraToolbarContent={extraToolbarContent}
          />
          <div
            className={cn(
              "editor-container relative resize-none p-4 flex-1 overflow-auto dark:bg-input/30",
              isDisabled ? "pointer-events-none" : "pointer-events-auto",
              fullSize ? "max-h-full" : "max-h-100"
            )}
          >
            {isRaw ? (
              <Textarea
                className="w-full h-full rounded-none resize-none outline-none bg-transparent p-0 border-none shadow-none focus-visible:ring-0 min-h-24"
                value={usedValue?.text || ""}
                onChange={(e) => {
                  const next = e.target.value;
                  updateLastValue(() => ({
                    text: next,
                    definition: {},
                    isRaw: true,
                    isControlled: true,
                  }));
                }}
                onSelect={(e) => {
                  const ta = e.target as HTMLTextAreaElement;
                  void ta.selectionStart;
                }}
                disabled={isDisabled}
                ref={rawTextareaRef}
              />
            ) : (
              <EditorContent editor={editor} autoFocus={autoFocus} />
            )}
          </div>

          {value && value.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
              {reasoningContent && (
                <Button
                  onClick={() => setIsReasoningOpen(true)}
                  size="icon"
                  variant="outline"
                  className={cn(
                    "w-8 h-8 rounded-full bg-background shadow-md",
                    "transition-transform duration-100 ease-in-out active:scale-90"
                  )}
                >
                  <Brain size={14} strokeWidth={1.5} />
                </Button>
              )}
              <div className="inline-flex items-stretch rounded-lg border border-border bg-background shadow-md">
                <button
                  onClick={handleRevertChange}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Undo2 size={15} />
                  Revert
                </button>
                <Separator orientation="vertical" />
                <button
                  onClick={handleAcceptChange}
                  disabled={isDisabled}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:text-foreground/80 transition-colors disabled:opacity-50"
                  tabIndex={-1}
                >
                  <Check size={15} className="text-emerald-600" />
                  Accept
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <AIDialogEditor
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          content={value || []}
          onSave={handleModalSave}
          chipOptions={chipOptions}
          unselectAllNodes={unselectAllNodes}
          label={label}
          isControlled={isControlled}
          enableHeuristics={enableHeuristics}
          initialHeuristicsOpen={dialogInitialHeuristicsOpen}
          heuristicResults={heuristicResults}
          isLoadingHeuristics={isLoadingHeuristics}
          isHeuristicsOutdated={isHeuristicsOutdated}
          isHeuristicsError={isHeuristicsError}
          heuristicsErrorMessage={heuristicsErrorMessage}
          canAnalyseHeuristics={canAnalyseHeuristics}
          triggerHeuristicEvaluation={triggerHeuristicEvaluation}
          scheduleHeuristicReanalysis={scheduleHeuristicReanalysis}
          revertHeuristicAnalysis={revertHeuristicAnalysis}
          onHeuristicTextChange={handleHeuristicTextChange}
          improverContext={improverContext}
        />
      )}
      {isReasoningOpen && (
        <ReasoningDialog
          isOpen={isReasoningOpen}
          onOpenChange={setIsReasoningOpen}
          content={reasoningContent}
        />
      )}
    </>
  );
};

export default AITiptapEditor;
